import {lazy, Suspense, useEffect } from 'react';
import { Route, Routes as ReactRouterRoutes, useNavigate } from 'react-router-dom';
import localStorageWrapped from '../common/localStorage';
import PageLoading from '../components/shared/pageLoading';

// App Bridge from CDN syncs the URL with the admin itself (history API),
// and dispatches 'shopify:navigate' for admin navigation (e.g. nav menu links)
function useAppBridgeRouting() {
  const navigate = useNavigate();

  useEffect(() => {
    const onNavigate = (event: Event) => {
      const href = (event.target as HTMLAnchorElement)?.getAttribute?.('href');

      if (href) {
        navigate(href);
      }
    };

    document.addEventListener('shopify:navigate', onNavigate);

    return () => document.removeEventListener('shopify:navigate', onNavigate);
  }, []);
}

const FORCE_ID = 'upsell-page-has-been-force-refreshed';

const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      localStorageWrapped.getItem(
        FORCE_ID
      ) || 'false'
    );

    try {
      const component = await componentImport();

      localStorageWrapped.setItem(
        FORCE_ID,
        'false'
      );

      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        localStorageWrapped.setItem(
          FORCE_ID,
          'true'
        );
        return window.location.reload();
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error;
    }
  });

const IndexPage = lazyWithRetry(() => import('../pages/index'));
const AddonsPage = lazyWithRetry(() => import('../pages/addons'));
const AnalyticsPage = lazyWithRetry(() => import('../pages/analytics'));
const InstallPage = lazyWithRetry(() => import('../pages/install'));
const PopupsPage = lazyWithRetry(() => import('../pages/popups'));
const SettingsPage = lazyWithRetry(() => import('../pages/settings'));
const HiddenPage = lazyWithRetry(() => import('../pages/hidden'));
const DiscountOfferPage = lazyWithRetry(() => import('../pages/discount_offer'));
const Page404 = lazyWithRetry(() => import('../pages/404'));

export function Routes() {
  useAppBridgeRouting();

  return (
    <Suspense fallback={<PageLoading/>}>
      <ReactRouterRoutes>
        <Route path="/" element={<IndexPage />} />
        <Route path="/addons" element={<AddonsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/index" element={<IndexPage />} />
        <Route path="/install" element={<InstallPage />} />
        <Route path="/popups" element={<PopupsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/hidden" element={<HiddenPage />} />
        <Route path="/discount_offer" element={<DiscountOfferPage />} />
        <Route element={<Page404 />} />
      </ReactRouterRoutes>
    </Suspense>
  );
}

export default Routes;
